import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import ErrorPage from './components/pages/Error';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/Layout';
import { AbilityProvider } from './hooks/abilityHooks';
import { matchRoute } from './config/routes';
import { getLoginRedirectFromPath } from './utils/url';
import { ProtectedRoute } from './components/ProtectedRoute';
import { loadPublicAppConfig } from './utils/runtimeConfig';
import './utils/zoomHelper';

export const RouteParamsContext = React.createContext<Record<string, string>>({});

const App = () => {
  const [routeKey, setRouteKey] = useState(
    () => window.location.pathname + window.location.search
  );
  const [routeMatch, setRouteMatch] = useState<{ route: any; params: Record<string, string> } | null>(null);

  useEffect(() => {
    const updateRoute = () => {
      const currentPath = window.location.pathname;

      // Bypass React Router for backend routes - let browser/proxy handle them
      if (currentPath.startsWith('/connect/') || currentPath.startsWith('/api/')) {
        return;
      }

      setRouteKey(currentPath + window.location.search);
      const match = matchRoute(currentPath);
      setRouteMatch(match);
    };

    updateRoute();
    const handleNavigation = () => {
      updateRoute();
    };

    window.addEventListener('popstate', updateRoute);
    window.addEventListener('navigate', handleNavigation);

    return () => {
      window.removeEventListener('popstate', updateRoute);
      window.removeEventListener('navigate', handleNavigation);
    };
  }, []);

  // Sync state with URL on mount (e.g. after full page reload)
  useEffect(() => {
    const currentPath = window.location.pathname;
    setRouteKey(currentPath + window.location.search);
    setRouteMatch(matchRoute(currentPath));
  }, []);

  // If this is a backend route, don't render anything - let browser handle navigation
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/connect/') || currentPath.startsWith('/api/')) {
    return null;
  }

  if (!routeMatch) {
    return (
      <ErrorPage
        title="Page Not Found"
        message="The page you are looking for does not exist."
        buttonText="Go to Login"
        redirectURL={getLoginRedirectFromPath(currentPath)}
      />
    );
  }

  const { route, params } = routeMatch;
  const Component = route.component;

  let content = <Component />;

  if (route.providers) {
    route.providers.forEach((Provider: any) => {
      content = <Provider>{content}</Provider>;
    });
  }

  if (route.requireAuth || route.requireOrg || route.requireOrgAdmin) {
    content = (
      <ProtectedRoute
        requireOrg={route.requireOrg}
        requireOrgAdmin={route.requireOrgAdmin}
      >
        {content}
      </ProtectedRoute>
    );
  }

  return (
    <RouteParamsContext.Provider value={params}>
      <AbilityProvider>
        <Layout>
          <div key={routeKey}>{content}</div>
        </Layout>
      </AbilityProvider>
    </RouteParamsContext.Provider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const isDevelopment = process.env.NODE_ENV === 'development';

const AppWrapper = isDevelopment ? (
  <React.StrictMode>
    <AuthProvider>
      <App />
    </AuthProvider>
  </React.StrictMode>
) : (
  <AuthProvider>
    <App />
  </AuthProvider>
);

loadPublicAppConfig()
  .then(() => {
    root.render(AppWrapper);
  })
  .catch((err) => {
    console.error('Failed to load public config:', err);
    root.render(
      <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif', maxWidth: 480 }}>
        <h1 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>Configuration error</h1>
        <p style={{ margin: 0, color: '#444' }}>
          The application could not load its configuration. Please refresh the page or try again later.
        </p>
      </div>
    );
  });
